"use client";
import {
  ZSL,
  canvasAtom,
  isEditDescriptionModalOpenAtom,
  isKeyboardShortcutsModalOpenAtom,
  isDeleteCanvasModalOpenAtom,
  BuiltInApp,
  Session,
  Account,
  togglesAtomResettable,
  Toggles,
  RawCanvas,
} from "@zapier/canvas-state";
import { CurrentAccountIdProvider } from "@zapier/identity";
import "@zapier/product-nav/dist/style.css";
import { Provider, createStore, useAtomValue, useAtom } from "jotai";
import "jotai-devtools/styles.css";
import { useHydrateAtoms } from "jotai/utils";
import { ReactNode } from "react";
import { ReactFlowProvider } from "@xyflow/react";

import { useBindUndoRedoKeyboardShortcuts } from "../../hooks/useBindUndoRedoKeyboardShortcuts";
import useUpdateSelectedApps from "../../hooks/useUpdateSelectedApps";
import { TitleUpdater } from "../TitleUpdater";
import { KeydownListeners } from "../KeydownListeners";
import KeyboardShortcutsModal from "../KeyboardShortcutsModal/KeyboardShortcutsModal";
import { Notifications } from "../Notifications";
import { EditDescriptionModal } from "../modals/EditDescriptionModal";
import { DeleteModal } from "../modals/DeleteModal";
import { CanvasHydrator } from "../CanvasHydrator";
import { useEmitPageViewEvent } from "./useEmitPageViewEvent";
import { HiddenProjectAssetsFetcher } from "../HiddenProjectAssetsFetcher";
import { BuilderNav } from "../BuilderNav";

import { JotaiDevTools } from "./JotaiDevtools";

const store = createStore();

type Props = {
  children: ReactNode;
  canvas?: RawCanvas;
  template?: ZSL;
  tablesApp: BuiltInApp;
  interfacesApp: BuiltInApp;
  chatbotsApp: BuiltInApp;
  account: Account;
  session: Session;
  toggles: Toggles;
};

export default function CanvasLayoutWrapper({
  children,
  canvas: canvasProp,
  template,
  tablesApp,
  interfacesApp,
  chatbotsApp,
  account,
  session,
  toggles,
}: Props) {
  const canvas = canvasProp ?? (template?.canvases[0] as RawCanvas);

  return (
    <CurrentAccountIdProvider>
      <ReactFlowProvider>
        <Provider store={store}>
          <CanvasHydrator
            canvas={canvas}
            account={account}
            session={session}
            template={template}
            tablesApp={tablesApp}
            interfacesApp={interfacesApp}
            chatbotsApp={chatbotsApp}
            toggles={toggles}
          >
            <CanvasLayout>
              <JotaiDevTools store={store} />
              <Notifications />
              <KeydownListeners />
              {children}
            </CanvasLayout>
          </CanvasHydrator>
        </Provider>
      </ReactFlowProvider>
    </CurrentAccountIdProvider>
  );
}

function CanvasLayout({ children }: { children: ReactNode }) {
  useEmitPageViewEvent();
  useHydrateAtoms([]);

  const [isEditDescriptionModalOpen, setIsEditDescriptionModalOpen] = useAtom(
    isEditDescriptionModalOpenAtom,
  );

  const [isDeleteCanvasModalOpen, setIsDeleteCanvasModalOpen] = useAtom(
    isDeleteCanvasModalOpenAtom,
  );

  const [isKeyboardShortcutsModalOpen, setIsKeyboardShortcutsModalOpen] =
    useAtom(isKeyboardShortcutsModalOpenAtom);

  const currentCanvas = useAtomValue(canvasAtom);

  useUpdateSelectedApps();
  useBindUndoRedoKeyboardShortcuts();

  const toggles = useAtomValue(togglesAtomResettable);

  return (
    <>
      <TitleUpdater title={currentCanvas.name || "Canvas"} />
      {toggles.manageProjectsMvp && <HiddenProjectAssetsFetcher />}
      <BuilderNav>{children}</BuilderNav>
      {isEditDescriptionModalOpen && (
        <EditDescriptionModal
          onClosed={() => setIsEditDescriptionModalOpen(false)}
        />
      )}
      {isDeleteCanvasModalOpen && (
        <DeleteModal onClosed={() => setIsDeleteCanvasModalOpen(false)} />
      )}
      {isKeyboardShortcutsModalOpen && (
        <KeyboardShortcutsModal
          onClosed={() => setIsKeyboardShortcutsModalOpen(false)}
        />
      )}
    </>
  );
}
